import {
  ELEMENTS_META,
  IComponent,
  LineItem,
  ProductListElementOptions,
} from '@gohighlevel/ghl-proposals-common'

import { useBuilderStore } from '@/store/builder'
import { useProductListStore } from '@/store/productListStore'
import { useElementOption } from './elementOptions'

export const useProductListActions = () => {
  const productListStore = useProductListStore()
  const builderStore = useBuilderStore()
  const { updateOption } = useElementOption()

  const getSelectableOptions = (key: keyof LineItem, value: boolean) => {
    if (key === 'optional') {
      if (value) {
        return { disabled: false }
      } else {
        return { disabled: true, selected: true }
      }
    }
    return {}
  }

  const computeUpdatedLineItems = (
    key: keyof LineItem,
    value: LineItem[keyof LineItem]
  ): LineItem[] | null => {
    const elementOptions = builderStore.activeElement
    const activeKey = productListStore.activeRow?.key

    if (!elementOptions || !activeKey) {
      return null
    }

    const canonicalLineItems: LineItem[] =
      (elementOptions?.component as IComponent<ProductListElementOptions>)
        ?.options?.lineItems || []

    // Find existing item to read fallback fields
    const existing = canonicalLineItems.find(it => it.key === activeKey)

    // Determine a "current price" to decide tax clearing behaviour
    const currentPrice =
      Number(productListStore.activeRow?.price ?? existing?.price ?? 0) || 0

    // If updating taxes while price <= 0, clear taxes
    let finalValue: typeof value = value
    if (key === 'taxes' && currentPrice <= 0) {
      finalValue = [] as any
    }

    // Build new canonical array (immutably)
    const updatedLineItems: LineItem[] = canonicalLineItems.map(
      (item: LineItem) => {
        if (item.key !== activeKey) return { ...item }

        const newItem: LineItem = {
          ...item,
          [key]: finalValue,
          ...getSelectableOptions(key, value as boolean),
        } as LineItem

        // If price gets set to <= 0, proactively clear taxes & normalize price/subtotal
        if (key === 'price' && Number(newItem.price as any) <= 0) {
          newItem.taxes = []
          newItem.price = 0
          newItem.subtotal = 0
        }

        // If taxes were changed but price (existing/new) <= 0, ensure taxes remain empty
        if (key === 'taxes') {
          const numericPrice = Number(newItem.price ?? item.price) || 0
          if (numericPrice <= 0) {
            newItem.taxes = []
          }
        }

        // Recompute subtotal whenever price or qty changes
        if (key === 'price' || key === 'qty') {
          const priceNum = Number(newItem.price ?? item.price) || 0
          const qtyNum = Number(newItem.qty ?? item.qty) || 0
          newItem.subtotal = priceNum * qtyNum
        }

        return newItem
      }
    )

    return updatedLineItems
  }

  const updateSelectedLineItem = (
    key: keyof LineItem,
    value: LineItem[keyof LineItem]
  ) => {
    const updatedLineItems = computeUpdatedLineItems(key, value)
    if (!updatedLineItems) return

    // Persist canonical options - this is what will be sent to backend
    updateOption(
      ELEMENTS_META.LINE_ITEMS,
      updatedLineItems,
      builderStore?.activeElementId as string
    )

    const activeKey = productListStore.activeRow?.key
    const newActiveItem = updatedLineItems.find(it => it.key === activeKey)
    if (newActiveItem) {
      productListStore.setActiveRow(
        newActiveItem,
        builderStore?.activeElementId || null
      )
    }
  }

  return { updateSelectedLineItem, computeUpdatedLineItems }
}
